import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useTranslation } from '../utils/i18n-utils';
import { useAuth } from '../context/AuthContext';
import {
  getAllUsers,
  getUserProfile,
  updateUserProfile,
  changePassword,
  deleteUser,
  createUser,
  User,
  UserProfile
} from '../api/user';
import { format } from 'date-fns';

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [users, setUsers] = useState<User[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Profile dialog state
  const [profileDialogOpen, setProfileDialogOpen] = useState<boolean>(false);
  const [profileFormData, setProfileFormData] = useState({
    username: ''
  });

  // Password change dialog state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState<boolean>(false);
  const [passwordFormData, setPasswordFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Create user dialog state
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState<boolean>(false);
  const [createUserFormData, setCreateUserFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    role: 'seller' as 'admin' | 'manager' | 'seller'
  });

  // Check if user is admin or manager (backend will enforce admin-only for API calls)
  useEffect(() => {
    if (currentUser && currentUser.role !== 'admin' && currentUser.role !== 'manager') {
      setError('Access denied. Admin or Manager role required.');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (currentUser && (currentUser.role === 'admin' || currentUser.role === 'manager')) {
      fetchUsers();
      fetchUserProfile();
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await getAllUsers();
      setUsers(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to fetch users:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load users. Please try again later.';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const profile = await getUserProfile();
      setUserProfile(profile);
      setProfileFormData({ username: profile.username });
    } catch (err: any) {
      console.error('Failed to fetch user profile:', err);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenProfileDialog = () => {
    setProfileDialogOpen(true);
  };

  const handleCloseProfileDialog = () => {
    setProfileDialogOpen(false);
    if (userProfile) {
      setProfileFormData({ username: userProfile.username });
    }
  };

  const handleUpdateProfile = async () => {
    try {
      if (!profileFormData.username.trim()) {
        showSnackbar('Username cannot be empty', 'error');
        return;
      }

      const updatedProfile = await updateUserProfile(profileFormData.username);
      setUserProfile(updatedProfile);
      showSnackbar('Profile updated successfully', 'success');
      setProfileDialogOpen(false);
      
      // Refresh users list to update current user's username
      fetchUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to update profile';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleChangePassword = async () => {
    try {
      if (!passwordFormData.currentPassword || !passwordFormData.newPassword) {
        showSnackbar('All fields are required', 'error');
        return;
      }

      if (passwordFormData.newPassword !== passwordFormData.confirmPassword) {
        showSnackbar('New passwords do not match', 'error');
        return;
      }

      if (passwordFormData.newPassword.length < 6) {
        showSnackbar('Password must be at least 6 characters long', 'error');
        return;
      }

      await changePassword({
        currentPassword: passwordFormData.currentPassword,
        newPassword: passwordFormData.newPassword
      });
      
      showSnackbar('Password changed successfully', 'success');
      setPasswordDialogOpen(false);
      setPasswordFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to change password';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleOpenDeleteDialog = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id);
      showSnackbar('User deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to delete user';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleOpenCreateUserDialog = () => {
    setCreateUserFormData({
      username: '',
      password: '',
      confirmPassword: '',
      role: 'seller'
    });
    setCreateUserDialogOpen(true);
  };

  const handleCloseCreateUserDialog = () => {
    setCreateUserDialogOpen(false);
    setCreateUserFormData({
      username: '',
      password: '',
      confirmPassword: '',
      role: 'seller'
    });
  };

  const handleCreateUser = async () => {
    try {
      if (!createUserFormData.username || !createUserFormData.password) {
        showSnackbar('Username and password are required', 'error');
        return;
      }

      if (createUserFormData.password !== createUserFormData.confirmPassword) {
        showSnackbar('Passwords do not match', 'error');
        return;
      }

      if (createUserFormData.password.length < 6) {
        showSnackbar('Password must be at least 6 characters long', 'error');
        return;
      }

      await createUser({
        username: createUserFormData.username,
        password: createUserFormData.password,
        role: createUserFormData.role
      });

      showSnackbar('User created successfully', 'success');
      handleCloseCreateUserDialog();
      fetchUsers();
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to create user';
      showSnackbar(errorMessage, 'error');
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'manager':
        return 'warning';
      case 'seller':
        return 'info';
      default:
        return 'default';
    }
  };

  // Don't render if user is not admin or manager
  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager')) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. Admin or Manager role required to view this page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700,
            mb: 0.5,
            color: '#101D68',
          }}
        >
          {t('userManagement.title') || 'User Management'}
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#6B7280',
          }}
        >
          Manage users and their access to the system
        </Typography>
      </Box>

      {/* User Profile Card */}
      <Card sx={{ 
        mb: 3,
        borderRadius: 3,
        boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography 
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#101D68',
              }}
            >
              {t('userManagement.myProfile') || 'My Profile'}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<PersonIcon />}
                onClick={handleOpenProfileDialog}
                sx={{ 
                  mr: 1,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#101D68',
                  color: '#101D68',
                  '&:hover': {
                    borderColor: '#0A1445',
                    backgroundColor: 'rgba(16, 29, 104, 0.05)',
                  },
                }}
              >
                {t('userManagement.editProfile') || 'Edit Profile'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<LockIcon />}
                onClick={handleOpenPasswordDialog}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#101D68',
                  color: '#101D68',
                  '&:hover': {
                    borderColor: '#0A1445',
                    backgroundColor: 'rgba(16, 29, 104, 0.05)',
                  },
                }}
              >
                {t('userManagement.changePassword') || 'Change Password'}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          {userProfile && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('userManagement.username') || 'Username'}
                </Typography>
                <Typography variant="body1">{userProfile.username}</Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('userManagement.role') || 'Role'}
                </Typography>
                <Chip
                  label={userProfile.role}
                  color={getRoleColor(userProfile.role) as any}
                  size="small"
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Paper sx={{
        borderRadius: 3,
        boxShadow: '0px 2px 8px rgba(16, 29, 104, 0.08)',
        overflow: 'hidden',
      }}>
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid #E5E7EB',
        }}>
          <Typography 
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#101D68',
            }}
          >
            {t('userManagement.allUsers') || 'All Users'}
          </Typography>
          {currentUser?.role === 'admin' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateUserDialog}
              sx={{
                background: 'linear-gradient(135deg, #101D68 0%, #2A3A8A 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0px 4px 12px rgba(16, 29, 104, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0A1445 0%, #101D68 100%)',
                  boxShadow: '0px 6px 16px rgba(16, 29, 104, 0.4)',
                },
              }}
            >
              {t('userManagement.createUser') || 'Create User'}
            </Button>
          )}
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('userManagement.username') || 'Username'}</TableCell>
                <TableCell>{t('userManagement.role') || 'Role'}</TableCell>
                <TableCell>{t('userManagement.createdAt') || 'Created At'}</TableCell>
                <TableCell align="right">{t('userManagement.actions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {t('userManagement.noUsers') || 'No users found'}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow 
                    key={user.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(16, 29, 104, 0.02)',
                      },
                      '& .MuiTableCell-body': {
                        borderBottom: '1px solid #E5E7EB',
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>{user.username}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        color={getRoleColor(user.role) as any}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          borderRadius: 1.5,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#6B7280' }}>
                      {format(new Date(user.createdAt), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      {user.id !== currentUser?.id && (
                        <Tooltip title={t('userManagement.delete') || 'Delete User'}>
                          <IconButton
                            color="error"
                            onClick={() => handleOpenDeleteDialog(user)}
                            size="small"
                            sx={{
                              '&:hover': {
                                backgroundColor: 'rgba(178, 34, 34, 0.1)',
                              },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Profile Update Dialog */}
      <Dialog open={profileDialogOpen} onClose={handleCloseProfileDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('userManagement.editProfile') || 'Edit Profile'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('userManagement.username') || 'Username'}
            fullWidth
            variant="outlined"
            value={profileFormData.username}
            onChange={(e) => setProfileFormData({ username: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseProfileDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleUpdateProfile} variant="contained">
            {t('common.save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('userManagement.changePassword') || 'Change Password'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('userManagement.currentPassword') || 'Current Password'}
            type="password"
            fullWidth
            variant="outlined"
            value={passwordFormData.currentPassword}
            onChange={(e) => setPasswordFormData({ ...passwordFormData, currentPassword: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label={t('userManagement.newPassword') || 'New Password'}
            type="password"
            fullWidth
            variant="outlined"
            value={passwordFormData.newPassword}
            onChange={(e) => setPasswordFormData({ ...passwordFormData, newPassword: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label={t('userManagement.confirmPassword') || 'Confirm New Password'}
            type="password"
            fullWidth
            variant="outlined"
            value={passwordFormData.confirmPassword}
            onChange={(e) => setPasswordFormData({ ...passwordFormData, confirmPassword: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleChangePassword} variant="contained">
            {t('common.save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>
          {t('userManagement.confirmDelete') || 'Confirm Delete'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t('userManagement.deleteMessage') || 'Are you sure you want to delete user'} 
            {' '}<strong>{userToDelete?.username}</strong>?
            <br />
            <br />
            {t('userManagement.deleteWarning') || 'This action cannot be undone. The user will lose access to the system immediately.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            {t('common.delete') || 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onClose={handleCloseCreateUserDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {t('userManagement.createUser') || 'Create New User'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label={t('userManagement.username') || 'Username'}
              value={createUserFormData.username}
              onChange={(e) => setCreateUserFormData({ ...createUserFormData, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('userManagement.password') || 'Password'}
              type="password"
              value={createUserFormData.password}
              onChange={(e) => setCreateUserFormData({ ...createUserFormData, password: e.target.value })}
              fullWidth
              required
              helperText={t('userManagement.passwordHelper') || 'Minimum 6 characters'}
            />
            <TextField
              label={t('userManagement.confirmPassword') || 'Confirm Password'}
              type="password"
              value={createUserFormData.confirmPassword}
              onChange={(e) => setCreateUserFormData({ ...createUserFormData, confirmPassword: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>{t('userManagement.role') || 'Role'}</InputLabel>
              <Select
                value={createUserFormData.role}
                label={t('userManagement.role') || 'Role'}
                onChange={(e) => setCreateUserFormData({ ...createUserFormData, role: e.target.value as 'admin' | 'manager' | 'seller' })}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="seller">Seller</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateUserDialog}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleCreateUser} variant="contained" color="primary">
            {t('common.create') || 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;

